import type { Capability } from "@remi/enums/capability";
import type { TaskType } from "@remi/enums/task-type";

import type { ResponsibleParty } from "./blocking-task-responsible-party";
import type { BlockingTaskType, ManualBlockingTaskType } from "./blocking-task-type";

export interface TaskTypeDisplay {
  name: string;
  action?: string;
  viewCompletedTaskLabel?: string;
  // One line on what the task is and what to do, shown under the task title on the task page.
  description?: string;
}

type NonEmptyReadonly<T> = readonly [T, ...T[]];

interface TaskPermission {
  read: NonEmptyReadonly<Capability>;
  update: NonEmptyReadonly<Capability>;
}

interface BlockingDefinition {
  display: TaskTypeDisplay;
  // Contractor resolve is granted separately via `can.task.resolveAllowlistedBlocker`, deliberately disjoint
  // from read and update: `can.task.read` feeds taskWhere and `can.task.update` gates many endpoints.
  permission: TaskPermission;
  contractorVisibleAsBlocker: boolean;
  responsibleParty: ResponsibleParty;
  // Title Case for thread "blocker created/resolved" copy, unlike display.name's sentence case. Hand-curated
  // because mechanical title-casing mangles acronyms (HOA, SOW, AHJ, PV, EPC, FSD).
  blockingLabel: string;
}

interface ManualBlockingDefinition extends BlockingDefinition {
  manualDescription: string;
}

interface SystemBlockingDefinition extends BlockingDefinition {
  manualDescription?: never;
}

interface WorkflowDefinition {
  display: TaskTypeDisplay & { action: string };
  permission: TaskPermission;
  // Set on workflow types too: ProjectBlocker.blockingTaskType is any task type that gates another. False
  // hides the card from contractors unless their own read gate passes; the internal record is gated by
  // canSeeInternalRecord.
  contractorVisibleAsBlocker?: boolean;
  responsibleParty?: never;
  blockingLabel?: never;
  manualDescription?: never;
}

type DefinitionFor<K extends TaskType> = K extends ManualBlockingTaskType
  ? ManualBlockingDefinition
  : K extends BlockingTaskType
    ? SystemBlockingDefinition
    : WorkflowDefinition;

export type TaskTypeDefinitions = { [K in TaskType]: DefinitionFor<K> };

const contractorTask = {
  read: ["task.read.contractor"],
  update: ["task.update.contractor"],
} as const satisfies TaskPermission;

const automationTask = {
  read: ["task.read.automation"],
  update: ["task.update.automation"],
} as const satisfies TaskPermission;

const financeTask = {
  read: ["task.read.finance"],
  update: ["task.update.finance"],
} as const satisfies TaskPermission;

const remiTask = {
  read: ["task.read.remi"],
  update: ["task.update.remi"],
} as const satisfies TaskPermission;

const TASK_TYPE_DEFINITIONS = {
  ACCEPT_BUYER_WORK_ORDER: {
    display: {
      name: "Accept buyer work order",
      action: "Complete",
      description: "Formally accept the partner's work order to open the job",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  ADDITIONAL_AHJ_REQUIREMENTS: {
    display: {
      name: "Additional AHJ requirements",
      description: "Blocked by additional requirements imposed by the AHJ",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "AHJ",
    blockingLabel: "Additional AHJ Requirements",
    manualDescription: "The AHJ has unique local requirements that are causing delays to the process.",
    permission: remiTask,
  },
  AHJ_AVAILABILITY_ISSUES: {
    display: {
      name: "AHJ availability issues",
      description: "Blocked by scheduling or availability problems at the AHJ",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "AHJ",
    blockingLabel: "AHJ Availability Issues",
    manualDescription:
      "The AHJ is having availability issues that is causing delays in permitting, scheduling inspections, etc.",
    permission: remiTask,
  },
  APPROVED_BY_AHJ: {
    display: {
      name: "Record and upload permit approval",
      action: "Record",
      viewCompletedTaskLabel: "View permit",
      description: "Upload the AHJ-approved permit to the project record",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  APPROVE_WORK_ORDER_CONCESSION: {
    display: {
      name: "Approve work order concession",
      viewCompletedTaskLabel: "View completion",
      action: "Review",
      description: "Finance is reviewing the adder Ops approved.",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Approve Work Order Concession",
    permission: {
      read: ["task.read.finance", "task.read.remi"],
      update: ["task.update.workOrderConcessionApproval"],
    },
  },
  ASSESSMENT_COMPLETED: {
    display: {
      name: "Record assessment completed",
      action: "Record",
      viewCompletedTaskLabel: "View results",
      description: "Mark the site assessment as finished and capture its results",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  ASSESSMENT_CONTRACTOR_ASSIGNED: {
    display: {
      name: "Assign assessment contractor",
      action: "Assign",
      description: "Assign the contractor who will perform the site assessment",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  ASSESSMENT_SCHEDULED: {
    display: {
      name: "Record assessment scheduled",
      action: "Record",
      description: "Log the scheduled date and time for the site assessment",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  ASSESS_QA_RESULTS: {
    display: {
      name: "Assess QA results",
      action: "Assess",
      viewCompletedTaskLabel: "View assessment",
      description: "Decide, per trade, whether the inspected work needs corrections",
    },
    permission: remiTask,
  },
  ASSIGN_CREW_TO_JOB: {
    display: {
      name: "Assign crew",
      action: "Assign",
      description: "Assign an available crew to the perform the scheduled job",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  AWAITING_HOMEOWNER_COLOR_SELECTION: {
    display: {
      name: "Awaiting homeowner color selection",
      action: "Resolve",
      description: "The homeowner's color selection was reset and the work order waits on their new selection",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Awaiting Homeowner Color Selection",
    permission: remiTask,
  },
  BALANCE_OUTSTANDING_OR_MILESTONE_DISPUTED: {
    display: {
      name: "Collect outstanding homeowner balance",
      description:
        "Blocked until Accounting collects the balance the homeowner still owes or resolves the rejected or clawed-back Paykeeper milestone",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Balance Outstanding or Milestone Disputed",
    permission: remiTask,
  },
  BUYER_WORK_ORDER_RECEIVED: {
    display: {
      name: "Buyer work order received",
      action: "Complete",
      description: "Log receipt of a partner-issued work order",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  CERTIFICATE_OF_COMPLETION_COMPLETED: {
    display: {
      name: "Collect signed certificate of completion",
      action: "Complete",
      description: "Collect the homeowner-signed Certificate of Completion",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  COC_AMOUNT_INCORRECT: {
    display: {
      name: "Correct the certificate of completion amount",
      description: "The homeowner reported the certificate of completion shows the wrong amount",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "CoC Amount Incorrect",
    permission: remiTask,
  },
  COC_COPY_CHANGE_REQUEST: {
    display: {
      name: "Update the certificate of completion wording",
      description: "The homeowner asked for wording on the certificate of completion to be changed before signing",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "CoC Copy Change Request",
    permission: remiTask,
  },
  COLLECT_HOMEOWNER_AVAILABILITY: {
    display: {
      name: "Collect homeowner availability",
      action: "Collect",
      viewCompletedTaskLabel: "View availability",
      description: "Collect the homeowner's availability so a site visit can be scheduled.",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  COLOR_SELECTION: {
    display: {
      name: "Record color selection from homeowner",
      action: "Record",
      description: "Capture the homeowner's material selection and color choice ahead of install",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.remi", "task.read.homeowner"],
      update: ["task.update.remi", "task.update.homeowner"],
    },
  },
  CONFIRM_APPEASEMENT_PAYMENT: {
    display: {
      name: "Confirm appeasement payment",
      action: "Confirm",
      description: "Confirm the appeasement payment has been issued or received",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  CONFIRM_CONTRACTOR: {
    display: {
      name: "Confirm contractor",
      action: "Assign",
      description: "Confirm the assigned contractor for the job before work begins",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  CONFIRM_PROJECT_IS_RTI: {
    display: {
      name: "Review and approve photos",
      action: "Review",
      viewCompletedTaskLabel: "View photos",
      description: "Review submitted photos and confirm the project is ready to invoice",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  CONFIRM_START: {
    display: {
      name: "Confirm work start date with homeowner",
      action: "Confirm",
      description: "Confirm the install start date directly with the homeowner",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  CONTRACTOR_PAID: {
    display: {
      name: "Record contractor paid",
      action: "View bills",
      viewCompletedTaskLabel: "View bills",
      description: "Confirm the contractor has been paid for their completed work",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  CONTRACTOR_RECEIPT_MISSING: {
    display: {
      name: "Contractor receipt missing",
      description: "Blocked until the contractor's missing receipt or invoice is collected and attached to the project",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Contractor Receipt Missing",
    permission: remiTask,
  },
  CONTRACTOR_REPORTED_SOW_ISSUE: {
    display: { name: "Resolve reported scope-of-work issue" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Contractor Reported SOW Issue",
    manualDescription:
      "The contractor reported that the scope of work does not match the job and it must be reviewed before work can proceed.",
    permission: remiTask,
  },
  CONTRACTOR_SOW_QUESTION: {
    display: { name: "Answer contractor scope-of-work question" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Contractor SOW Question",
    manualDescription:
      "The contractor has a question about the scope of work that Remi must answer before work can proceed.",
    permission: remiTask,
  },
  CORRECTIONS_COMPLETED: {
    display: {
      name: "Record corrections completed",
      action: "Record",
      description: "Log the day that corrections were completed at this worksite",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  CREATE_INVOICE: {
    display: {
      name: "Create invoice",
      action: "Begin",
      viewCompletedTaskLabel: "View earnings",
      description: "Review the project's profit and loss, then generate the invoice in QuickBooks",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  CREATE_VENDOR_CREDITS: {
    display: { name: "Create vendor credit", action: "Create vendor credits", viewCompletedTaskLabel: "View details" },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  CREATE_WORK_ORDER: {
    display: {
      name: "Create work order",
      action: "Create",
      viewCompletedTaskLabel: "View work order",
      description: "Create the work order for this project",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  CUSTOMER_COMPLAINT: {
    display: {
      name: "Customer complaint tier 1",
      action: "Resolve",
      description: "Address a first-tier customer complaint",
    },
    permission: {
      read: ["task.read.contractorComplaint"],
      update: ["task.update.remi"],
    },
  },
  CUSTOMER_COMPLAINT_TIER_2: {
    display: {
      name: "Customer complaint tier 2",
      action: "Resolve",
      description: "Handle an escalated complaint requiring manager involvement",
    },
    permission: {
      read: ["task.read.contractorComplaint"],
      update: ["task.update.remi"],
    },
  },
  CUSTOMER_COMPLAINT_TIER_3: {
    display: {
      name: "Customer complaint tier 3",
      action: "Resolve",
      description: "Handle a high-severity complaint requiring leadership involvement",
    },
    permission: {
      read: ["task.read.contractorComplaint"],
      update: ["task.update.remi"],
    },
  },
  CUSTOMER_NO_SHOW: {
    display: {
      name: "Customer no-show",
      description: "Blocked because the customer did not show for a scheduled appointment",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Customer No-Show",
    manualDescription: "Customer did now show up for scheduled appointment",
    permission: remiTask,
  },
  DAMAGED_PV_EQUIPMENT: {
    display: { name: "Resolve damaged solar equipment" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Damaged PV Equipment",
    manualDescription:
      "Solar equipment on the work site is damaged and must be resolved before the contractor can proceed.",
    permission: remiTask,
  },
  DETERMINE_CORRECTIONS: {
    display: {
      name: "Determine corrections",
      action: "Begin",
      viewCompletedTaskLabel: "View results",
      description: "Identify what corrections are needed and why",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  EAGLE_VIEW_ORDERED: {
    display: {
      name: "Order and upload EagleView",
      action: "Order",
      viewCompletedTaskLabel: "View EagleView",
      description: "Order the EagleView report and attach it to the project",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  FINALIZE_AMOUNT_COVERED_BY_BUYER: {
    display: {
      name: "Finalize amount approved by partner",
      action: "Finalize",
      description: "Confirm the final amount the partner will cover on the job",
    },
    permission: remiTask,
  },
  FINALIZE_ASSESSMENT_RESULTS: {
    display: {
      name: "Finalize assessment results",
      action: "Finalize",
      viewCompletedTaskLabel: "View results",
      description: "Record assessment findings and finalize the resulting scope of work",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  FINALIZE_CONTRACT: {
    display: {
      name: "Finalize contract",
      action: "Finalize",
      description: "Confirm the homeowner or partner contract has been finalized",
    },
    contractorVisibleAsBlocker: true,
    permission: automationTask,
  },
  FINALIZE_CORRECTIONS: {
    display: {
      name: "Finalize corrections",
      action: "Begin",
      viewCompletedTaskLabel: "View finalization",
      description: "Confirm corrections were completed or if further work is required",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  FINALIZE_FUNDING: {
    display: {
      name: "Finalize funding",
      action: "Finalize",
      description: "Confirm funding has been finalized for the project",
    },
    contractorVisibleAsBlocker: true,
    permission: automationTask,
  },
  FINALIZE_QUOTE: {
    display: {
      name: "Finalize scope of work change",
      action: "Finalize",
      description: "Review updated quote to ensure it reflects the correct scope-of-work change",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  FINALIZE_WORK_ORDER: {
    display: {
      name: "Finalize work order",
      action: "Complete",
      viewCompletedTaskLabel: "View work order",
      description: "Finalize the work order after all edits and adjustments are complete",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  FINAL_INSPECTION_COMPLETED: {
    display: {
      name: "Record final inspection results",
      action: "Record",
      viewCompletedTaskLabel: "View results",
      description: "Record the outcome of the AHJ final inspection",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  FINAL_INSPECTION_SCHEDULED: {
    display: {
      name: "Schedule final inspection",
      action: "Schedule",
      description: "Book the AHJ final inspection appointment",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  FINANCIAL_RESPONSIBILITY_UNDECIDED: {
    display: {
      name: "Decide financial responsibility",
      description:
        "Blocked until Account Management rules whether Remi invoices the partner, charges back the contractor or absorbs the cost",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Financial Responsibility Undecided",
    permission: remiTask,
  },
  FINDINGS_APPROVED: {
    display: {
      name: "Findings approved",
      action: "Verify",
      description: "Verify that submitted findings have been approved by the partner",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  FUNDING_NOT_SECURED: {
    display: { name: "Secure project funding", description: "Blocked until the money for this work is in place" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Funding Not Secured",
    permission: remiTask,
  },
  GET_PARTNER_SCOPE_APPROVAL: {
    display: {
      name: "Get partner scope approval",
      description: "Get the partner's approval of the extra scope the job needs, so quoting can go on",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "BUYER",
    blockingLabel: "Partner Scope Approval Needed",
    permission: remiTask,
  },
  GET_PARTNER_SCOPE_INFORMATION: {
    display: {
      name: "Get partner scope information",
      description: "Get the information the partner's scope of work leaves out, so quoting can go on",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "BUYER",
    blockingLabel: "Partner Scope Missing Information",
    permission: remiTask,
  },
  GET_QUOTING_HELP: {
    display: {
      name: "Get quoting help",
      description: "Get help from your teammates with a quote that can't be made confidently yet",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Quoting Help Needed",
    permission: remiTask,
  },
  HOMEOWNER_DECISION_DELAY: {
    display: {
      name: "Homeowner decision delay",
      description: "Blocked while the homeowner is still deciding on next steps",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Homeowner Decision Delay",
    manualDescription:
      "The homeowner wants to proceed but needs to temporarily delay progress due to personal circumstances (e.g., going out of town, dealing with an unexpected issue). This is a temporary hold with intent to resume.",
    permission: remiTask,
  },
  HOMEOWNER_REFUSES_TO_PAY: {
    display: {
      name: "Decide on lien or collections",
      description:
        "Blocked until Accounting decides whether to file a lien or send the homeowner's unpaid balance to collections",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Homeowner Refuses to Pay",
    permission: remiTask,
  },
  HOMEOWNER_SOW_REJECTION: {
    display: {
      name: "Homeowner SOW rejection",
      description: "Blocked because the homeowner has rejected the scope of work",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Homeowner SOW Rejection",
    manualDescription:
      "After hearing Remi's SOW, the homeowner has not approved the work or had a different understanding of the work.",
    permission: remiTask,
  },
  HOMEOWNER_UNRESPONSIVE: {
    display: {
      name: "Homeowner unresponsive",
      description: "Blocked because the homeowner is not responding to outreach",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Homeowner Unresponsive",
    manualDescription:
      "The homeowner associated with the project is currently unresponsive after several attempts. Remi will be unable to move forward with the SOW until contact can be made.",
    permission: remiTask,
  },
  HOMEOWNER_UNWILLING_TO_MOVE_FORWARD: {
    display: {
      name: "Homeowner unwilling to move forward",
      description: "Blocked because the homeowner is not willing to proceed",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Homeowner Unwilling to Move Forward",
    manualDescription: "The homeowner has decided they do not want to move forward with the project at all.",
    permission: remiTask,
  },
  INSTALL_COMPLETED: {
    display: {
      name: "Record work completed",
      action: "Record",
      description: "Record the date the crew finished work onsite",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  INSTALL_SCHEDULED: {
    display: {
      name: "Schedule work",
      action: "Schedule",
      viewCompletedTaskLabel: "View appointment",
      description:
        "Select a date to schedule the project within SLA. Please confirm the date with the homeowner before completing this task.",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  INSTALL_STARTED: {
    display: {
      name: "Record work started",
      action: "Record",
      description: "Confirm that the crew has begun work onsite",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  INSURANCE_APPROVAL_RECEIVED: {
    display: {
      name: "Record carrier decision",
      action: "Record",
      description: "Log the carrier's evaluation of the submitted estimate",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.insurance.approval"],
      update: ["task.update.insurance.approval"],
    },
  },
  INSURANCE_ELECTIVE_ESTIMATE_SUBMITTED: {
    display: {
      name: "Review elective estimate",
      action: "Review",
      description: "Review elective estimate before submitting to quoting",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.insurance.estimateReview"],
      update: ["task.update.insurance.estimateReview"],
    },
  },
  INSURANCE_ELECTIVE_WORK_CREATED: {
    display: {
      name: "Create elective estimate",
      action: "Create",
      description: "Estimate for elective work indicated by the homeowner",
    },
    permission: remiTask,
  },
  INSURANCE_ESTIMATE_CREATED: {
    display: {
      name: "Create carrier estimate",
      action: "Create",
      viewCompletedTaskLabel: "View estimate",
      description: "Estimate for the indicated cause of loss and optionally annotate site visit photos",
    },
    permission: {
      read: ["task.read.insurance.assessment"],
      update: ["task.update.insurance.assessment"],
    },
  },
  INSURANCE_ESTIMATE_SUBMITTED: {
    display: {
      name: "Review and submit carrier estimate",
      action: "Review",
      description: "Review created estimate to ensure it meets Remi standards and submit to carrier",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.insurance.estimateReview"],
      update: ["task.update.insurance.estimateReview"],
    },
  },
  INSURANCE_REVISION_ELECTIVE_ESTIMATE_CREATED: {
    display: {
      name: "Revise elective estimate",
      action: "Revise",
      description: "Update the elective estimate to reflect new scope or pricing",
    },
    permission: {
      read: ["task.read.insurance.assessment"],
      update: ["task.update.insurance.assessment"],
    },
  },
  INSURANCE_REVISION_ELECTIVE_ESTIMATE_SUBMITTED: {
    display: {
      name: "Review revised elective estimate",
      action: "Review",
      description: "Review the revised elective estimate to ensure it meets Remi standards",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.insurance.estimateReview"],
      update: ["task.update.insurance.estimateReview"],
    },
  },
  INSURANCE_REVISION_ESTIMATE_CREATED: {
    display: {
      name: "Revise carrier estimate",
      action: "Revise",
      description: "Revise estimate to include supplemental work",
    },
    permission: {
      read: ["task.read.insurance.assessment"],
      update: ["task.update.insurance.assessment"],
    },
  },
  INSURANCE_REVISION_ESTIMATE_SUBMITTED: {
    display: {
      name: "Review revised carrier estimate",
      action: "Review",
      description: "Review revised estimate with supplemental work to ensure it meets Remi standards",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.insurance.estimateReview"],
      update: ["task.update.insurance.estimateReview"],
    },
  },
  INTRO_CALL_COMPLETED: {
    display: {
      name: "Confirm scope of work with homeowner",
      action: "Confirm",
      description: "Call the homeowner to walk through and confirm the scope before work starts",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.homeowner"],
      update: ["task.update.homeowner"],
    },
  },
  INVOICE_APPROVED: {
    display: {
      name: "Invoice approved",
      action: "Verify",
      description: "Verify that the submitted invoice has been approved by the partner",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  INVOICE_NEEDS_ACCOUNTING_REVIEW: {
    display: {
      name: "Invoice needs accounting review",
      description: "Blocked until Accounting reviews the flagged invoice",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Invoice Needs Accounting Review",
    permission: remiTask,
  },
  INVOICE_PAID: {
    display: { name: "Record invoice paid", action: "Record", description: "Confirm the invoice has been paid" },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  ITEL_RECEIVED: {
    display: {
      name: "Upload ITEL report",
      action: "Upload",
      viewCompletedTaskLabel: "View ITEL report",
      description: "Upload the ITEL material identification report",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  MATERIALS_DELAY: {
    display: {
      name: "Contractor-ordered materials delay",
      description: "Blocked while materials the contractor ordered from their own supplier are delayed in arriving",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "CONTRACTOR",
    blockingLabel: "Materials Delay",
    manualDescription:
      "The project has been delayed due to waiting on materials to arrive or become in stock that are associated with the SOW.",
    permission: remiTask,
  },
  MATERIAL_MATCH_PENDING: {
    display: {
      name: "Material match (ITEL) pending",
      description: "Blocked until the ITEL material-match report comes back and is attached to the project",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Material Match (ITEL) Pending",
    permission: remiTask,
  },
  MISQUOTE_CORRECTIONS: {
    display: { name: "Misquote corrections", description: "Blocked by corrections needed after a scope was misquoted" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Misquote Corrections",
    manualDescription:
      "A post-NTP quote error was discovered that needs to be resolved before Remi can move forward with the project.",
    permission: remiTask,
  },
  MISSING_FINDINGS: {
    display: {
      name: "Missing findings",
      description:
        "Blocked because the contractor hasn't reported back the findings needed to move the project forward",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "CONTRACTOR",
    blockingLabel: "Missing Findings",
    manualDescription:
      "This will mostly relate to RFRs, but an RFR completion could be delayed if we still have no findings back from the contractor.",
    permission: remiTask,
  },
  NON_REMI_PV_INSTALL_COMPLETED: {
    display: {
      name: "Non-Remi PV work completed",
      action: "Record",
      description: "Record completion of solar PV work completed by a non-Remi contractor outside of Remi",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  NON_REMI_PV_INSTALL_SCHEDULED: {
    display: {
      name: "Non-Remi PV work scheduled",
      action: "Record",
      viewCompletedTaskLabel: "View",
      description: "Log the scheduled date for solar PV work completed by a non-Remi contractor",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Non-Remi PV Install Scheduled",
    permission: remiTask,
  },
  NON_REMI_ROOF_WORK_COMPLETED: {
    display: {
      name: "Non-Remi work completed",
      action: "Record",
      description: "Record completion of roof work completed by a non-Remi contractor",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.remi", "task.update.contractorAdmin"],
    },
  },
  NON_REMI_ROOF_WORK_SCHEDULED: {
    display: {
      name: "Non-Remi work scheduled",
      action: "Record",
      viewCompletedTaskLabel: "View",
      description: "Blocked until non-Remi roof work is scheduled by the outside party",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Non-Remi Roof Work Scheduled",
    permission: contractorTask,
  },
  NOTICE_TO_PROCEED: {
    display: {
      name: "Verify NTP received",
      action: "Complete",
      viewCompletedTaskLabel: "View status",
      description: "Verify that homeowner has signed their HIC and funded",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  ORDER_MATERIALS: {
    display: {
      name: "Order materials",
      action: "Order",
      description: "Order materials needed for the install and record order ID",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  PARTNER_APPROVAL_PENDING: {
    display: {
      name: "Partner approval pending",
      description: "Blocked until Sunrun, or the insurer on insurance jobs, approves what Remi submitted",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Partner Approval Pending",
    permission: remiTask,
  },
  PARTNER_DISPATCH_NOT_READY: {
    display: {
      name: "Get the partner dispatch ready",
      action: "Resolve",
      description: "Blocked until the partner's dispatch or work order exists and is accepted and editable",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "BUYER",
    blockingLabel: "Partner Dispatch Not Ready",
    permission: remiTask,
  },
  PARTNER_DISPUTE_WITH_HOMEOWNER: {
    display: {
      name: "Partner dispute with homeowner",
      description: "Blocked until the partner rules on its dispute with the homeowner",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "BUYER",
    blockingLabel: "Partner Dispute With Homeowner",
    permission: remiTask,
  },
  PARTNER_NEEDS_CORRECTED_DOCUMENTS: {
    display: {
      name: "Correct documents for the partner",
      description: "Blocked until the estimate, quote and certificate of completion agree and the partner accepts them",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Partner Needs Corrected Documents",
    permission: remiTask,
  },
  PAYKEEPER_MILESTONE_NOT_CLAIMED: {
    display: {
      name: "PayKeeper milestone not claimed",
      action: "Resolve",
      description:
        "The automatic claim and its retry didn't reach PayKeeper. Check that the project has completed-work photos and a signed HIC, fix what's missing, then complete this task to re-submit the claim.",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "PayKeeper Milestone Not Claimed",
    permission: remiTask,
  },
  PAYKEEPER_MILESTONE_STALLED_IN_REVIEW: {
    display: {
      name: "Milestone stalled in PayKeeper review",
      action: "Resolve",
      description:
        "PayKeeper disputed this milestone or hasn't verified it within its review window. Read PayKeeper's latest note on the milestone. If it's disputed, fix the evidence it asks for, then complete this task to re-submit the claim. Otherwise follow up with PayKeeper.",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Milestone Stalled in PayKeeper Review",
    permission: remiTask,
  },
  PAYKEEPER_PAYMENT_APPROVED: {
    display: {
      name: "PayKeeper payment approved",
      action: "Verify",
      description:
        "Waits for PayKeeper to verify this project's milestone, which releases the payout. Completes on its own.",
    },
    permission: remiTask,
  },
  PAYKEEPER_SUBMIT_FINDINGS: {
    display: {
      name: "Submit PayKeeper findings",
      action: "Verify",
      description:
        "Remi claims this project's PayKeeper milestone with its completed-work photos and signed HIC when Review revenue completes. This completes on its own once PayKeeper shows the milestone claimed.",
    },
    permission: remiTask,
  },
  PENDING_BUSINESS_LICENSING: {
    display: {
      name: "Pending business licensing",
      description: "Blocked awaiting a required business license to be issued",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "CONTRACTOR",
    blockingLabel: "Pending Business Licensing",
    manualDescription:
      "The assigned contractor is unable to move forward with permitting until they have completed or renewed their business licensing with the AHJ.",
    permission: remiTask,
  },
  PENDING_CANCELLATION_REVIEW: {
    display: { name: "Pending cancellation review", description: "Blocked awaiting review of a cancellation request" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Pending Cancellation Review",
    manualDescription:
      "At least one work order or active FSD has been canceled, and the Remi team must verify the cancellation before proceeding. While this review is in progress, all other active projects will also be placed on hold.",
    permission: remiTask,
  },
  PENDING_EPC_TO_SCHEDULE_PV_FOR_BACKFILL: {
    display: {
      name: "Pending EPC to schedule PV for backfill",
      description: "Blocked awaiting the EPC to schedule the PV backfill install",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Pending EPC to Schedule PV for Backfill",
    manualDescription:
      "Remi is unable to schedule the backfill while we are waiting to hear back from EPC on the PV install being scheduled. This will need to take place first before the task flow for Remi can be moved forward.",
    permission: remiTask,
  },
  PENDING_ESCROW_FUNDING_CONFIRMATION: {
    display: {
      name: "Pending escrow funding confirmation",
      action: "Confirm",
      description: "Confirm escrow has released funding for the project",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  PENDING_ESTIMATE_ASSETS: {
    display: {
      name: "Receive sketch",
      action: "Complete",
      viewCompletedTaskLabel: "View completion",
      description:
        "Waiting on the Hover measurement job or EagleView report so the carrier estimate can be created. Complete it manually if the sketch arrived by hand",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  PENDING_FUNDING: {
    display: { name: "Pending funding", description: "Blocked awaiting project funding to be secured" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Pending Funding",
    manualDescription:
      "Pending funding. The homeowner's payment is pending or failed and needs to be resolved before moving forward.",
    permission: remiTask,
  },
  PENDING_HOA_APPROVAL: {
    display: { name: "Pending HOA approval", description: "Blocked awaiting HOA approval on the planned work" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Pending HOA Approval",
    manualDescription:
      "The homeowner can not move forward with the SOW until they receive approval from their homeowner's association",
    permission: remiTask,
  },
  PENDING_INSPECTION_PROOF_OF_PASS: {
    display: {
      name: "Pending inspection proof of pass",
      description: "Blocked awaiting documentation that an inspection passed",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "CONTRACTOR",
    blockingLabel: "Pending Inspection Proof of Pass",
    manualDescription:
      "The contractor or AHJ has not yet provided the proof of passed inspection that is delaying our ability to move the project forward.",
    permission: remiTask,
  },
  PENDING_LOAN_STIPS: {
    display: {
      name: "Pending loan stipulations",
      description: "Blocked awaiting resolution of outstanding loan stipulations",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Pending Loan Stips",
    manualDescription:
      "Pending loan stipulations. The homeowner's Sunlight financing has outstanding conditions that must be resolved before moving forward.",
    permission: remiTask,
  },
  PENDING_REINSPECTION: {
    display: { name: "Pending reinspection", description: "Blocked awaiting a scheduled reinspection" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Pending Reinspection",
    manualDescription:
      "A previous site visit was found to be insufficient. The property needs to be reinspected before work can proceed.",
    permission: remiTask,
  },
  PENDING_RELATED_PROJECT_COMPLETION_TO_INVOICE: {
    display: {
      name: "Pending related project completion to invoice",
      description: "Blocked from invoicing until a related project completes",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "RELATED_PROJECT",
    blockingLabel: "Pending Related Project Completion to Invoice",
    manualDescription:
      "This is used when we can't invoice a project because we need another project to be completed so they can be invoiced together.",
    permission: remiTask,
  },
  PENDING_RELATED_PROJECT_CORRECTIONS: {
    display: {
      name: "Corrections pending",
      description: "Blocked by corrections outstanding on a related sibling project",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "RELATED_PROJECT",
    blockingLabel: "Pending Related Project Corrections",
    manualDescription:
      "A correction project is in progress and must complete before this project can be marked ready-to-invoice.",
    permission: remiTask,
  },
  PENDING_RELATED_PROJECT_OTHER: {
    display: {
      name: "Pending related project other",
      description: "Blocked by an unspecified issue on a related sibling project",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "RELATED_PROJECT",
    blockingLabel: "Pending Related Project (Other)",
    manualDescription:
      "A related Remi or non-Remi (i.e. Solar) project is currently delaying this project due to a non-scheduling, non-permitting issue.",
    permission: remiTask,
  },
  PENDING_RELATED_PROJECT_PERMITTING: {
    display: {
      name: "Pending related project permitting",
      description: "Blocked by permitting on a related sibling project",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "RELATED_PROJECT",
    blockingLabel: "Pending Related Project Permitting",
    manualDescription:
      "A related Remi or non-Remi (i.e. Solar) project is currently delaying this project due to permitting.",
    permission: remiTask,
  },
  PENDING_RELATED_PROJECT_SCHEDULING: {
    display: {
      name: "Pending related project scheduling",
      description: "Blocked by scheduling on a related sibling project",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "RELATED_PROJECT",
    blockingLabel: "Pending Related Project Scheduling",
    manualDescription:
      "A related Remi or non-Remi (i.e. Solar) project is currently delaying this project due to scheduling of install or inspection.",
    permission: remiTask,
  },
  PENDING_SALES_PROCESS: {
    display: { name: "Pending sales process", description: "Blocked while the sales process is still in progress" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Pending Sales Process",
    manualDescription:
      "This blocker task represents that the project can not move forward while a Remi sales rep works on finalizing the services that the homeowner would like to move forward with from Remi.",
    permission: remiTask,
  },
  PENDING_SYSTEM_REDESIGN: {
    display: {
      name: "Pending system redesign",
      description: "Blocked awaiting a solar system redesign from the partner",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Pending System Redesign",
    manualDescription: "The PV system design needs to be redesigned before the project can move forward.",
    permission: remiTask,
  },
  PERMIT_COMPLETED: {
    display: {
      name: "Apply for permit",
      action: "Complete",
      description: "Submit the permit application to the local AHJ",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  PRE_ASSESSMENT_CALL_COMPLETED: {
    display: {
      name: "Confirm pre-assessment with homeowner",
      action: "Complete",
      description: "Confirm pre-assessment appointment details directly with the homeowner",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  PRE_EXISTING_PERMITS: {
    display: {
      name: "Pre-existing permits",
      description: "Blocked by pre-existing permits that must be resolved before starting",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Pre-Existing Permits",
    manualDescription:
      "The AHJ will not allow the contractor to pull permits because there is currently an existing permit out for the site.",
    permission: remiTask,
  },
  PROPERTY_MODEL_DETAIL_ADDED: {
    display: {
      name: "Add property model details",
      action: "Add",
      viewCompletedTaskLabel: "View completion",
      description: "Open the damage map and add property model details for a site visit.",
    },
    permission: remiTask,
  },
  QUOTE_CREATED: {
    display: {
      name: "Create quote",
      action: "Create",
      viewCompletedTaskLabel: "View quotes",
      description: "Create a quote based on the scope of work indicated",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  QUOTE_ON_SUPERSEDED_XA_ESTIMATE: {
    display: {
      name: "Re-quote on the current XactAnalysis estimate",
      description:
        "Blocks Create Invoice until the active quote reconciles to an estimate XactAnalysis still corroborates",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Quote on Superseded XA Estimate",
    permission: remiTask,
  },
  QUOTE_XA_ESTIMATE_MISMATCH: {
    display: {
      name: "Reconcile quote with Xactanalysis estimate",
      description: "Blocked until the quote's insurance-covered amount reconciles to a carrier Xactanalysis estimate",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Quote / XA Estimate Mismatch",
    permission: remiTask,
  },
  RATE_CONTRACTOR_COORDINATION: {
    display: {
      name: "Rate contractor coordination",
      action: "Rate",
      description: "Rate how well the contractor coordinated on the project",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  RECEIVED_THIRD_PARTY_QUOTE_APPROVAL: {
    display: {
      name: "Received third party quote approval",
      action: "Complete",
      description: "Record that a third party has approved the quote",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  RECEIVE_ROOF_ITEL: {
    display: { name: "Receive roof ITEL", action: "Upload", viewCompletedTaskLabel: "View roof ITEL report" },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  RECEIVE_SIDING_ITEL: {
    display: { name: "Receive siding ITEL", action: "Upload", viewCompletedTaskLabel: "View siding ITEL report" },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  RECORD_UPSELL_RESULTS: {
    display: {
      name: "Send agreement",
      action: "Start agreement",
      viewCompletedTaskLabel: "View agreement",
      description:
        "Record the homeowner's funding method, property access details, and any special requests, then send them the HIC to sign",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  REDESIGN_RECEIVED: {
    display: {
      name: "Receive redesign",
      action: "Review",
      viewCompletedTaskLabel: "View completion",
      description: "Review a solar system redesign received from the partner",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  REMI_ORDERED_MATERIALS_DELAY: {
    display: {
      name: "Resolve Remi-ordered materials delay",
      description: "Blocked until materials Remi ordered arrive, or the wrong ones are replaced",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Remi-Ordered Materials Delay",
    permission: remiTask,
  },
  REQUOTE_OR_ABANDON_SOW_CHANGE: {
    display: {
      name: "Decide on declined SOW change addendum",
      action: "Decide",
      description: "Blocked until Account Management requotes or abandons the SOW change the homeowner declined",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "SOW Change Addendum Declined",
    permission: remiTask,
  },
  RESOLVE_PARTNER_SCOPE_MISMATCH: {
    display: {
      name: "Resolve partner scope mismatch",
      description: "Act on the partner's correction or confirmation of a scope that disagrees with the site",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "BUYER",
    blockingLabel: "Partner Scope Doesn't Match Site",
    permission: remiTask,
  },
  // Visible to contractors deliberately, though Quoting owns it: the contractor's work is held and the
  // card is what tells them they are waiting on Remi to pull the CAD rather than on themselves.
  RETRIEVE_CAD: {
    display: {
      name: "Retrieve CAD",
      description:
        "Blocked until Sunrun's CAD layout is on the removal and reinstall work. If the CAD is already on the project, tag that file “CAD layout” and this task closes itself.",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Retrieve CAD",
    permission: remiTask,
  },
  REVIEW_AND_ACCEPT_WORK_ORDER: {
    display: {
      name: "Review and accept work order",
      action: "Review work order",
      viewCompletedTaskLabel: "View work order",
      description: "Accept or reject assigned work order",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  REVIEW_AND_CLOSE_WORK_ORDER: {
    display: {
      name: "Review and close work order",
      action: "Begin",
      viewCompletedTaskLabel: "View earnings",
      description: "Review a completed work order and close it out for billing",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  REVIEW_APPEASEMENT: {
    display: {
      name: "Review appeasement",
      action: "Review",
      description: "Review a proposed appeasement (concession) to a homeowner",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  REVIEW_BID: {
    display: {
      name: "Review bid",
      action: "Review",
      viewCompletedTaskLabel: "View bid",
      description: "Review SOW and determine if any additional processes are needed",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  REVIEW_CHANGE_ORDER_REQUEST: {
    display: {
      name: "Review change order request",
      action: "Review",
      description: "Price the billable line items on each pending contractor change order and approve it",
    },
    permission: remiTask,
  },
  REVIEW_FINANCIALS_FOR_CANCELLATION: {
    display: {
      name: "Review financials for cancellation",
      action: "Begin",
      description: "Review the project's profit and loss before finalizing the cancellation",
    },
    contractorVisibleAsBlocker: true,
    permission: financeTask,
  },
  REVIEW_FINANCIAL_ADJUSTMENTS: {
    display: {
      name: "Review financial adjustments",
      action: "Review",
      description:
        "Confirm all disputes, credit memos, and adjuster bills on the project are resolved, then sync the adjustments to QuickBooks",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.financeAdmin"],
      update: ["task.update.financeAdmin"],
    },
  },
  REVIEW_REVENUE: {
    display: {
      name: "Review revenue",
      action: "Review",
      viewCompletedTaskLabel: "View earnings",
      description:
        "Select the work order(s) to invoice, confirm the HIC is complete, and generate the accounting journal entries for the project's revenue",
    },
    contractorVisibleAsBlocker: true,
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  REVIEW_WORK_ORDER_REJECTION: {
    display: {
      name: "Review work order rejection",
      viewCompletedTaskLabel: "View completion",
      action: "Review",
      description:
        "The contractor asked for a work order concession or reported a scope problem instead of accepting. Review it and decide how the work order moves forward.",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Review Work Order Rejection",
    permission: remiTask,
  },
  ROOF_LEAK: {
    display: { name: "Roof leak", action: "Resolve", description: "Investigate and resolve a reported roof leak" },
    permission: {
      read: ["task.read.contractorComplaint"],
      update: ["task.update.remi"],
    },
  },
  SCHEDULE_REINSPECTION: {
    display: {
      name: "Schedule a reinspection",
      description: "Schedule the reinspection the property needs, so the carrier estimate can be finished",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Reinspection Needed",
    permission: remiTask,
  },
  SCOPE_SELECTED: {
    display: {
      name: "Select scope",
      action: "Select",
      viewCompletedTaskLabel: "View completion",
      description: "Open the damage map and select the scope of work.",
    },
    permission: remiTask,
  },
  SELECT_PV_SYSTEMS_FOR_REDESIGN: {
    display: {
      name: "Select PV systems for redesign",
      action: "Complete",
      viewCompletedTaskLabel: "View selection",
      description: "Choose which PV systems need to be included in the redesign",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SEND_APPEASEMENT: {
    display: {
      name: "Send appeasement",
      action: "Send",
      viewCompletedTaskLabel: "View details",
      description: "Send the approved appeasement to the homeowner",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SET_CUSTOM_QUOTE_PRICE: {
    display: {
      name: "Set custom quote price",
      description: "Set the custom price the job needs, so the quote can be created",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Custom Pricing Required",
    permission: remiTask,
  },
  SITE_VISIT_DETAILS_RECORDED: {
    display: {
      name: "Record site visit details",
      action: "Record",
      viewCompletedTaskLabel: "View details",
      description: "Log the observations and details captured during a site visit",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  SITE_VISIT_RESULTS_ANALYZED: {
    display: {
      name: "Analyze site visit results",
      action: "Analyze",
      viewCompletedTaskLabel: "View results",
      description: "Determine if there is additional work to quote",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SOURCING_CONTRACTOR: {
    display: { name: "Sourcing contractor", description: "Blocked while a contractor is being sourced for the job" },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Sourcing Contractor",
    manualDescription: "A contractor needs to be sourced and assigned before the project can move forward.",
    permission: remiTask,
  },
  SOW_ADDITIONAL_APPROVAL_NEEDED: {
    display: {
      name: "SOW additional approval needed",
      description:
        "Blocked because Remi found additional scope not captured in the original request, pending approval before quoting can proceed",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "SOW Additional Approval Needed",
    manualDescription:
      "Remi has discovered additional SOW details that were not captured by the quote requestor originally. Remi can not move forward with quote creation until the additional SOW is approved.",
    permission: remiTask,
  },
  SOW_CHANGE_BUILD_QUOTE: {
    display: {
      name: "Build SOW change quote",
      action: "Build",
      description: "Build the quote version for this scope-of-work change",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SOW_CHANGE_FINALIZE_QUOTE: {
    display: {
      name: "Finalize SOW change quote",
      action: "Finalize",
      description: "Review the updated quote to ensure it reflects the correct scope-of-work change",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SOW_CHANGE_REQUEST: {
    display: { name: "SOW change request", description: "Blocked by a pending scope-of-work change request" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "SOW Change Request",
    permission: remiTask,
  },
  SOW_CHANGE_REVIEW: {
    display: {
      name: "Review SOW change",
      action: "Review",
      description:
        "Check the requested scope change against the active quote and the selected projects, then confirm it",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SOW_CHANGE_SEND_ADDENDUM: {
    display: {
      name: "Send SOW change addendum",
      action: "Send",
      description: "Send the homeowner the change order addendum for this scope-of-work change",
    },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SOW_CHANGE_VERIFY: {
    display: {
      name: "Verify SOW change",
      action: "Verify",
      viewCompletedTaskLabel: "View status",
      description:
        "Verify the homeowner signed any addendum and the bid is funded; the selected projects stay on hold until then",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "SOW Change",
    permission: remiTask,
  },
  SOW_DISCREPANCY: {
    display: {
      name: "SOW discrepancy",
      description: "Blocked by a discrepancy between the scope of work and site conditions",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "SOW Discrepancy",
    manualDescription:
      "The scope of work provided to Remi contains inconsistencies or discrepancies. These discrepancies need to be resolved before a quote can be created.",
    permission: remiTask,
  },
  SOW_DISCREPANCY_REVIEW: {
    display: {
      name: "SOW discrepancy review",
      description: "Blocked while Estimating identifies the discrepancy between the scope of work and site conditions",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "SOW Discrepancy Review",
    manualDescription:
      "The scope of work doesn't match the job and the specific discrepancy must be identified before the project can move forward.",
    permission: remiTask,
  },
  SOW_MISSING_INFO: {
    display: {
      name: "SOW missing info",
      description: "Blocked because the scope of work is missing information needed to create a quote",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "SOW Missing Info",
    manualDescription:
      "The scope of work provided to Remi is missing critical info that prevents Remi from moving forward and creating a quote.",
    permission: remiTask,
  },
  STAMPED_PLANS_REQUIRED: {
    display: {
      name: "Stamped plans required",
      description: "Blocked until the partner provides PE-stamped structural drawings for the roof work",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Stamped Plans Required",
    permission: remiTask,
  },
  SUBMIT_FINDINGS: {
    display: {
      name: "Submit findings",
      action: "Submit",
      description: "Submit project findings to the partner portal after revenue review",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  SUBMIT_ROOF_ITEL: {
    display: { name: "Submit roof ITEL", action: "Begin", viewCompletedTaskLabel: "View roof ITEL" },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SUBMIT_SIDING_ITEL: {
    display: { name: "Submit siding ITEL", action: "Begin", viewCompletedTaskLabel: "View siding ITEL" },
    contractorVisibleAsBlocker: true,
    permission: remiTask,
  },
  SUNRUN_FINDINGS_APPROVED: {
    display: {
      name: "Sunrun findings approved",
      action: "Verify",
      description: "Verify that submitted findings have been approved by Sunrun",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  SUNRUN_FINDINGS_REJECTED: {
    display: {
      name: "Sunrun findings rejected",
      action: "Resolve",
      description: "Sunrun rejected the submitted findings and they must be corrected and resubmitted",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Sunrun Findings Rejected",
    permission: remiTask,
  },
  SUNRUN_FINDINGS_STALLED_IN_REVIEW: {
    display: {
      name: "Findings stalled in Sunrun review",
      action: "Resolve",
      description: "Submitted findings have sat in Sunrun review past the expected turnaround",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Findings Stalled in Sunrun Review",
    permission: remiTask,
  },
  SUNRUN_FINDINGS_SUBMISSION_BLOCKED: {
    display: {
      name: "Sunrun findings submission blocked",
      action: "Resolve",
      description: "Findings cannot be submitted to Sunrun until the work order or FSD state is resolved",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Sunrun Findings Submission Blocked",
    permission: remiTask,
  },
  SUNRUN_INVOICE_APPROVED: {
    display: {
      name: "Sunrun invoice approved",
      action: "Verify",
      description: "Verify that the submitted invoice has been approved by Sunrun",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  SUNRUN_INVOICE_APPROVED_NOT_PAID: {
    display: {
      name: "Sunrun invoice approved but not paid",
      action: "Resolve",
      description: "Sunrun approved the invoice but payment has not arrived past the expected turnaround",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Sunrun Invoice Approved but Not Paid",
    permission: remiTask,
  },
  SUNRUN_INVOICE_REJECTED: {
    display: {
      name: "Sunrun invoice rejected",
      action: "Resolve",
      description: "Sunrun rejected the submitted invoice and it must be corrected and resubmitted",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Sunrun Invoice Rejected",
    permission: remiTask,
  },
  SUNRUN_INVOICE_STALLED_IN_REVIEW: {
    display: {
      name: "Invoice stalled in Sunrun review",
      action: "Resolve",
      description: "The submitted invoice has sat in Sunrun review past the expected turnaround",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Invoice Stalled in Sunrun Review",
    permission: remiTask,
  },
  SUNRUN_PAID_INVOICE_OUTSTANDING: {
    display: {
      name: "Sunrun paid invoice outstanding",
      action: "Resolve",
      description: "Sunrun paid the invoice but it still reads as outstanding in Remi",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Sunrun Paid Invoice Outstanding",
    permission: remiTask,
  },
  SUNRUN_SUBMIT_FINDINGS: {
    display: {
      name: "Submit Sunrun findings",
      action: "Submit",
      description: "Submit project findings to Sunrun after revenue review",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  SUNRUN_WORK_ORDER_APPROVED: {
    display: {
      name: "Sunrun work order approved",
      action: "Verify",
      description: "Verify that the work order has been approved by Sunrun",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  SUNRUN_WORK_ORDER_REJECTED: {
    display: {
      name: "Sunrun work order rejected",
      action: "Resolve",
      description: "Sunrun rejected the work order and it must be corrected and resubmitted",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Sunrun Work Order Rejected",
    permission: remiTask,
  },
  SUNRUN_WORK_ORDER_REJECTED_QA: {
    display: {
      name: "Sunrun work order rejected for QA",
      action: "Resolve",
      description:
        "Sunrun rejected the work order over a findings or other non-invoice issue that QA must resolve before resubmission",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Sunrun Work Order Rejected (QA)",
    permission: remiTask,
  },
  UNPERMITTED_STRUCTURE: {
    display: {
      name: "Unpermitted structure",
      description: "Blocked because an unpermitted structure was discovered on the property",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "HOMEOWNER",
    blockingLabel: "Unpermitted Structure",
    manualDescription:
      "The AHJ will not allow the contractor to pull permits because there is an unpermitted structure on the work site that needs to be permitted or removed first.",
    permission: remiTask,
  },
  UNSIGNED_HIC_AFTER_NTP: {
    display: {
      name: "Get the active HIC signed",
      action: "Resolve",
      description: "Notice to proceed is complete, but the active quote's HIC or change order is not signed",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Unsigned HIC",
    permission: remiTask,
  },
  UPLOADED_COMPLETED_WORK_PHOTOS: {
    display: {
      name: "Submit work photos",
      action: "Upload",
      viewCompletedTaskLabel: "View photos",
      description: "Upload photos of the completed work for QA review",
    },
    contractorVisibleAsBlocker: true,
    permission: contractorTask,
  },
  VERIFY_CONTRACTOR_COSTS: {
    display: {
      name: "Verify contractor costs",
      action: "Begin",
      viewCompletedTaskLabel: "View earnings",
      description: "Verify the contractor's reported costs before authorizing payment",
    },
    contractorVisibleAsBlocker: true,
    permission: financeTask,
  },
  WAITING_ON_BUYER_TO_APPROVE_FSD_ACTION: {
    display: {
      name: "Waiting on solar company to approve FSD action",
      description: "Blocked awaiting the solar company's approval on an FSD action",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "BUYER",
    blockingLabel: "Waiting on Buyer to Approve FSD Action",
    manualDescription:
      "Remi has sent initial findings to EPC, and Remi is currently waiting for EPC to approve further action for the home based on the findings (i.e. we are recommending a full removal, waiting for approval).",
    permission: remiTask,
  },
  WESTHILL_DOCUMENTS_ATTACHED: {
    display: {
      name: "Westhill documents attached",
      action: "Verify",
      description:
        "Verify that XactAnalysis has the claim's Certificate of Completion, HIC and (with a signed CoC) photo completion report. Remi checks the claim's XA documents when Create Invoice completes and again the next business morning, and completes this on its own when everything is there. If it's Ready, the latest note lists what XA is missing: upload it to the claim in XactAnalysis, then mark this done.",
    },
    permission: remiTask,
  },
  WESTHILL_FINDINGS_APPROVED: {
    display: {
      name: "Westhill findings approved",
      action: "Verify",
      description:
        "Verify that Westhill's CES has reviewed the findings and forwarded the claim to its Payment Specialist. Completes on its own from the Westhill thread. If it's Ready, read the thread and record an attempt.",
    },
    permission: remiTask,
  },
  WESTHILL_FINDINGS_STALLED_IN_REVIEW: {
    display: {
      name: "Findings stalled in Westhill review",
      action: "Resolve",
      description:
        "Findings have sat in Westhill CES review past the expected turnaround. Read the latest Westhill note first: if Westhill asked Remi for something, get it to them; otherwise follow up with Westhill.",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Findings Stalled in Westhill Review",
    permission: remiTask,
  },
  WESTHILL_INVOICE_APPROVED: {
    display: {
      name: "Westhill invoice approved",
      action: "Verify",
      description:
        "Verify that Westhill's Payment Specialist has approved the invoice for payment. Completes on its own from the Westhill thread or when the invoice is paid. If it's Ready, read the thread and record an attempt.",
    },
    permission: remiTask,
  },
  WESTHILL_INVOICE_STALLED_IN_REVIEW: {
    display: {
      name: "Invoice stalled in Westhill review",
      action: "Resolve",
      description:
        "The invoice has sat with Westhill's Payment Specialist past the expected turnaround. Read the latest Westhill note first: if Westhill asked Remi for something, get it to them; otherwise follow up with Westhill.",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Invoice Stalled in Westhill Review",
    permission: remiTask,
  },
  WORKMANSHIP_ISSUE: {
    display: {
      name: "Workmanship issue",
      action: "Resolve",
      description: "Resolve a workmanship defect flagged after install",
    },
    permission: {
      read: ["task.read.contractorComplaint"],
      update: ["task.update.remi"],
    },
  },
  WORK_COMPLETION_DISPUTE: {
    display: {
      name: "Resolve work completion dispute",
      description: "The homeowner disputes that the work on the certificate of completion was finished",
    },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Work Completion Dispute",
    permission: remiTask,
  },
  WORK_ORDER_APPROVED: {
    display: {
      name: "Work order approved",
      action: "Verify",
      description: "Verify that the work order has been approved by the partner",
    },
    permission: {
      read: ["task.read.remi"],
      update: ["task.update.finance"],
    },
  },
  WORK_ORDER_COST_DISPUTE: {
    display: {
      name: "Work order cost dispute",
      description: "Blocked until Work Orders resolves the difference between the contractor's bill and the work order",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "Work Order Cost Dispute",
    permission: remiTask,
  },
  WORK_ORDER_UNCLEAR: {
    display: { name: "Clarify work order" },
    contractorVisibleAsBlocker: true,
    responsibleParty: "REMI",
    blockingLabel: "Work Order Unclear",
    manualDescription: "The work order is unclear and needs clarification from Remi before the contractor can proceed.",
    permission: remiTask,
  },
  XA_ESTIMATE_MISMATCH: {
    display: {
      name: "Reconcile Remi estimate with XactAnalysis",
      description: "Blocks Create Invoice until every XactAnalysis estimate total matches a Remi estimate",
    },
    contractorVisibleAsBlocker: false,
    responsibleParty: "REMI",
    blockingLabel: "XA Estimate Mismatch",
    permission: remiTask,
  },
} as const satisfies TaskTypeDefinitions;

export const DEFINITIONS_BY_TYPE: Readonly<TaskTypeDefinitions> = TASK_TYPE_DEFINITIONS;
